import { useRef, useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, Divider, Hidden, List, ListItem, ListItemText, Popover, Typography } from '@mui/material';
import InboxTwoToneIcon from '@mui/icons-material/InboxTwoTone';
import { lighten, styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import AccountBoxTwoToneIcon from '@mui/icons-material/AccountBoxTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
// Classic (new look) account menu: thin outlined icons, like Claude / ChatGPT.
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useTheme } from '@mui/material/styles';
import { useChromeMenuSx } from '../Menu';
import { IEmployeeProfile } from 'src/Common/Interfaces/Profile/IEmployeeProfile';
import { getEffectiveRoles } from 'src/Common/navigation/developerAccess';
import { notifyAuthTokenChanged } from 'src/Common/settings/deviceIdentity';
import { clearImageUrlCache } from 'src/Common/utils/imageUtils';

const UserBoxButton = styled(Button)(({ theme }) => `
        padding-left: ${theme.spacing(1)};
        padding-right: ${theme.spacing(1)};
`);

const MenuUserBox = styled(Box)(({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`);

const UserBoxText = styled(Box)(({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`);

const UserBoxLabel = styled(Typography)(({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`);

const UserBoxDescription = styled(Typography)(({ theme }) => `
        color: ${lighten(theme.palette.secondary.main, 0.5)};
`);

function HeaderUserbox() {
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  
  // Retrieve profile data from sessionStorage
  const profileData = JSON.parse(sessionStorage.getItem('profileData') || '[]');
  const employeeProfile = JSON.parse(localStorage.getItem('employeeProfile') || '{}') as IEmployeeProfile;
  // Check if profileData is an array and has at least one item
  const user = profileData.length > 0 ? profileData[0] : {};

  // The stored profile is the decoded login token, whose claims are camelCase (firstName,
  // lastName) — the PascalCase fields of IEmployeeProfile are never there, so the name and title
  // used to render blank. There is no designation claim: the first role stands in for it.
  const claims = employeeProfile as IEmployeeProfile & { firstName?: string; lastName?: string };
  const firstName = (claims.FirstName || claims.firstName || '').trim();
  const lastName = (claims.LastName || claims.lastName || '').trim();
  const firstRole = getEffectiveRoles()[0] || '';
  const userProfile = {
    name: [firstName, lastName].filter(Boolean).join(' '),
    avatar: user.Photo || '',
    jobtitle: employeeProfile.Designation || firstRole.replace(/([a-z])([A-Z])/g, '$1 $2')
  };
  // Initials drawn locally — the avatar used to be fetched from ui-avatars.com on every page.
  const initials = ((firstName.charAt(0) + lastName.charAt(0)) || '?').toUpperCase();
  const storeType = localStorage.getItem('storeType') || '';

  // Visual-only: null under Full Modern (its account menu stays exactly as it is); on the
  // classic path the compact Claude / ChatGPT account button and a flat hairline menu.
  const theme = useTheme();
  const chrome = useChromeMenuSx();
  const isModern = Boolean((theme as any).isModern);
  const k = chrome?.k;
  const classicLabelSx = k ? { fontSize: 14, fontWeight: 500, color: k.text, lineHeight: 1.25 } : undefined;
  const classicDescSx = k ? { fontSize: 12.5, color: k.muted, lineHeight: 1.35 } : undefined;
  const classicMenuListSx = k
    ? {
        p: 0,
        '& .MuiListItem-root': {
          minHeight: 36,
          px: '10px',
          py: '6px',
          my: '1px',
          gap: '10px',
          borderRadius: '8px',
          color: k.text,
          '& > .MuiSvgIcon-root': { fontSize: 18, minWidth: 0, color: k.navText },
          '& .MuiListItemText-primary': { fontSize: 14, fontWeight: 400, color: k.text },
          '&:hover, &.active': { bgcolor: k.hover },
          '&.active .MuiListItemText-primary': { fontWeight: 500 }
        }
      }
    : undefined;

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleSignOut = (): void => {
    // Clear sessionStorage and localStorage
    sessionStorage.removeItem('authData');
    sessionStorage.removeItem('profileData');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRoles');
    localStorage.removeItem('employeeProfile');
    localStorage.removeItem('restrictToBilling');
    // Same-tab sign-out: the hub connection and the device session stop (the ledger stays).
    notifyAuthTokenChanged();
    clearImageUrlCache();

    // Redirect to the login page
    navigate('/login');
  };


  return (
    <>
      <UserBoxButton
        color="secondary"
        ref={ref}
        onClick={handleOpen}
        sx={
          k
            ? {
                height: 40,
                minWidth: 0,
                px: '6px',
                py: 0,
                borderRadius: '8px',
                color: k.text,
                fontWeight: 400,
                '&:hover': { bgcolor: k.hover },
                '&.Mui-focusVisible': { outline: `2px solid ${k.accent}`, outlineOffset: '-2px' }
              }
            : undefined
        }
      >
        {/* <Avatar variant="rounded" alt={userProfile.name} src={userProfile.avatar} /> */}

        <Avatar
          variant="rounded"
          alt={userProfile.name}
          src={userProfile.avatar || undefined}
          sx={k ? { width: 32, height: 32, borderRadius: '50%', fontSize: 13 } : undefined}
        >
          {initials}
        </Avatar>


        <Hidden mdDown>
          <UserBoxText sx={k ? { pl: '8px', pr: '2px' } : undefined}>
            <UserBoxLabel variant="body1" sx={classicLabelSx}>{userProfile.name}</UserBoxLabel>
            <UserBoxDescription variant="body2" sx={classicDescSx}>{userProfile.jobtitle}</UserBoxDescription>
          </UserBoxText>
        </Hidden>
        <Hidden smDown>
          {k ? (
            <KeyboardArrowDownRoundedIcon sx={{ ml: '4px', fontSize: 18, color: k.muted }} />
          ) : (
            <ExpandMoreTwoToneIcon sx={{ ml: 1 }} />
          )}
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          // Classic: drops below the account button (Modern keeps opening over it).
          vertical: isModern ? 'top' : 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={chrome ? { sx: { ...chrome.paper, mt: '6px', width: 264, maxWidth: '95vw' } } : undefined}
      >
        <MenuUserBox
          sx={
            k
              ? { minWidth: 210, background: 'transparent', p: '10px 10px 8px', alignItems: 'center' }
              : { minWidth: 210 }
          }
          display="flex"
        >
          {/* <Avatar variant="rounded" alt={userProfile.name} src={userProfile.avatar} /> */}
          <Avatar
            variant="rounded"
            alt={userProfile.name}
            src={userProfile.avatar || undefined}
            sx={k ? { width: 36, height: 36, borderRadius: '50%', fontSize: 14, flexShrink: 0 } : undefined}
          >
            {initials}
          </Avatar>


          <UserBoxText sx={k ? { pl: '10px', minWidth: 0 } : undefined}>
            <UserBoxLabel variant="body1" sx={classicLabelSx}>{userProfile.name}</UserBoxLabel>
            <UserBoxDescription variant="body2" sx={classicDescSx}>{userProfile.jobtitle}</UserBoxDescription>
            <UserBoxDescription variant="body2" sx={classicDescSx}>{employeeProfile.branchName}</UserBoxDescription>
            {storeType && (
              <UserBoxDescription variant="body2" sx={classicDescSx}>Store Type: {storeType}</UserBoxDescription>
            )}
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={k ? { my: '4px', bgcolor: k.border } : { mb: 0 }} />
        <List sx={classicMenuListSx ?? { p: 1 }} component="nav">
          <ListItem button to="/management/profile/details" component={NavLink}>
            {k ? <PersonOutlineRoundedIcon fontSize="small" /> : <AccountBoxTwoToneIcon fontSize="small" />}
            <ListItemText primary="My Profile" />
          </ListItem>
          <ListItem button to="/dashboards/messenger" component={NavLink}>
            {k ? <ForumOutlinedIcon fontSize="small" /> : <InboxTwoToneIcon fontSize="small" />}
            <ListItemText primary="Messenger" />
          </ListItem>
          <ListItem button to="/management/profile/settings" component={NavLink}>
            {k ? <ManageAccountsOutlinedIcon fontSize="small" /> : <AccountTreeTwoToneIcon fontSize="small" />}
            <ListItemText primary="Account Settings" />
          </ListItem>
        </List>
        <Divider sx={k ? { my: '4px', bgcolor: k.border } : undefined} />
        <Box sx={k ? { m: 0 } : { m: 1 }}>
          <Button
            color="primary"
            fullWidth
            onClick={handleSignOut}
            sx={
              k
                ? {
                    justifyContent: 'flex-start',
                    gap: '10px',
                    minHeight: 36,
                    px: '10px',
                    py: '6px',
                    borderRadius: '8px',
                    fontSize: 14,
                    fontWeight: 400,
                    color: k.text,
                    '&:hover': { bgcolor: k.hover },
                    '& .MuiSvgIcon-root': { fontSize: 18, color: k.navText }
                  }
                : undefined
            }
          >
            {k ? <LogoutRoundedIcon /> : <LockOpenTwoToneIcon sx={{ mr: 1 }} />}
            Sign out
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
